'use client';

import NextLink from 'next/link';
import { usePathname, useRouter, useSearchParams as useNextSearchParams } from 'next/navigation';
import { useEffect, type AnchorHTMLAttributes, type ReactNode } from 'react';
import { stripLanguagePrefix } from '../i18n/urlLanguage';

type To = string;
type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'className'> & {
  to: To;
  children?: ReactNode;
  className?: string;
  state?: unknown;
};

type NavigateOptions = {
  replace?: boolean;
};

export function Link({ to, children, state: _state, ...props }: LinkProps) {
  if (/^https?:\/\//.test(to)) {
    return (
      <a href={to} {...props}>
        {children}
      </a>
    );
  }

  return (
    <NextLink href={to} {...props}>
      {children}
    </NextLink>
  );
}

type NavLinkProps = Omit<LinkProps, 'className'> & {
  end?: boolean;
  className?: string | ((state: { isActive: boolean }) => string);
};

export function NavLink({ to, end, className, children, ...props }: NavLinkProps) {
  const pathname = usePathname() || '/';
  const targetPath = to.split('?')[0] || '/';
  const isActive = end
    ? pathname === targetPath
    : pathname === targetPath || pathname.startsWith(`${targetPath}/`);
  const resolvedClassName = typeof className === 'function' ? className({ isActive }) : className;

  return (
    <Link to={to} className={resolvedClassName} {...props}>
      {children}
    </Link>
  );
}

export function useNavigate() {
  const router = useRouter();

  return (to: string | number, options?: NavigateOptions) => {
    if (typeof to === 'number') {
      window.history.go(to);
      return;
    }

    if (options?.replace) router.replace(to);
    else router.push(to);
  };
}

export function useLocation() {
  const pathname = usePathname() || '/';
  const searchParams = useNextSearchParams();
  const search = searchParams.toString();

  return {
    pathname,
    search: search ? `?${search}` : '',
    hash: typeof window !== 'undefined' ? window.location.hash : '',
    state: null,
  };
}

export function useParams<
  T extends Record<string, string | undefined> = Record<string, string | undefined>,
>() {
  const pathname = stripLanguagePrefix(usePathname() || '/');
  const segments = pathname.split('/').filter(Boolean);
  const params: Record<string, string | undefined> = {};

  if (segments[0] === 'stores' && segments[1]) {
    params.slug = segments[1];
  }

  if (segments[0] === 'piece' && segments[1]) {
    params.id = segments[1];
  }

  if (segments[0] === 'news' && segments[1] === 'external' && segments[2]) {
    params.article_id = segments[2];
  } else if (segments[0] === 'news' && segments[1]) {
    params.slug = segments[1];
  }

  return params as T;
}

export function useSearchParams(): [URLSearchParams] {
  const searchParams = useNextSearchParams();
  return [new URLSearchParams(searchParams.toString())];
}

export function Navigate({ to, replace }: { to: string; replace?: boolean }) {
  const navigate = useNavigate();

  useEffect(() => {
    navigate(to, { replace });
  }, [navigate, replace, to]);

  return null;
}
